<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { Calendar, ChevronLeft, ChevronRight, X } from "utruckin-icons";

// types and props
interface ISelectedDates {
  fromDate: Date;
  toDate: Date;
}

interface IDatePicker {
  mode: "single" | "range";
  isTwoSided?: boolean;
  maxDaysRange?: number;
  defaultRange?: ISelectedDates;
  placement?: "bottom" | "top" | "left" | "right";
  clearable?: boolean;
  selectedDate?: Date;
}

const props = withDefaults(defineProps<IDatePicker>(), {
  mode: "single",
  maxDaysRange: 0,
  isTwoSided: false,
  placement: "bottom",
});

const emit = defineEmits<{
  (
    e: "update",
    startDate?: Date | null,
    endDate?: Date | null,
    selectedDate?: Date | null
  ): void;
}>();

// ref
const currentMonth = ref(new Date());
const startDate = ref<Date | null>(props.defaultRange?.fromDate ?? null);
const endDate = ref<Date | null>(props.defaultRange?.toDate ?? null);
const hoverDate = ref<Date | null>(null);
const pickerEl = ref<HTMLElement | null>(null);
const isOpen = ref(false);
const selectedDate = ref<Date | null>(props.defaultRange?.selectedDate ?? null);
const viewMode = ref("days");
const activePane = ref("left");

// computed

const rightMonth = computed(() => {
  return new Date(
    currentMonth.value.getFullYear(),
    currentMonth.value.getMonth() + 1,
    1
  );
});

const calendarDays = computed(() => {
  const year = currentMonth.value.getFullYear();
  const month = currentMonth.value.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const days = [];

  for (let i = 0; i < firstDay; i++) {
    days.push({ date: null });
  }

  for (let i = 1; i <= daysInMonth; i++) {
    days.push({ date: i, fullDate: new Date(year, month, i) });
  }

  return days;
});

const calendarDaysRight = computed(() => {
  const year = rightMonth.value.getFullYear();
  const month = rightMonth.value.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push({ date: null });
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({ date: i, fullDate: new Date(year, month, i) });
  }
  return days;
});

const yearsStart = ref(new Date().getFullYear() - 8);

const visibleYears = computed(() =>
  Array.from({ length: 18 }, (_, i) => yearsStart.value + i)
);

const syncYearsStartToCurrent = () => {
  yearsStart.value = currentMonth.value.getFullYear() - 8;
};

// functions
const outsideDatePickerClick = (e: MouseEvent) => {
  if (isOpen.value && !pickerEl.value?.contains(e.target as Node)) {
    if (props.mode === "range" && startDate.value && !endDate.value)
      startDate.value = null;
    hoverDate.value = null;
    isOpen.value = false;
  }
};

const onKeyboardNav = (e: KeyboardEvent) => {
  if (!isOpen.value || viewMode.value !== "days") return;

  if (e.key === "ArrowLeft") {
    e.preventDefault();
    currentMonth.value = new Date(
      currentMonth.value.getFullYear(),
      currentMonth.value.getMonth() - 1,
      1
    );
  } else if (e.key === "ArrowRight") {
    e.preventDefault();
    currentMonth.value = new Date(
      currentMonth.value.getFullYear(),
      currentMonth.value.getMonth() + 1,
      1
    );
  }
};

const onClickEsc = (e: KeyboardEvent) => {
  if (e.key === "Escape") {
    if (props.mode === "range" && startDate.value && !endDate.value)
      startDate.value = null;
    hoverDate.value = null;
    isOpen.value = false;
  }
  onKeyboardNav(e);
};

const dayNow = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

const clearCalendar = () => {
  emit("update", startDate.value, endDate.value, selectedDate.value);
};

// hooks
onMounted(() => {
  document.addEventListener("mousedown", outsideDatePickerClick);
  window.addEventListener("keydown", onClickEsc);
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", outsideDatePickerClick);
  window.removeEventListener("keydown", onClickEsc);
});

watch(
  () => isOpen.value,
  (newVal) => {
    if (!newVal) {
      emit("update", startDate.value, endDate.value, selectedDate.value);
    }
  }
);
</script>

<template>
  <div class="c-co-date-picker" ref="pickerEl">
    <!-- Input -->
    <div
      class="c-co-date-picker__input"
      :class="isOpen ? 'active' : ''"
      @click="
        isOpen
          ? props.mode === 'range' &&
            startDate &&
            !endDate &&
            (startDate = null)((isOpen = false))
          : ((currentMonth = new Date(
              (
                (props.mode === 'single' ? selectedDate : startDate) ||
                new Date()
              ).getFullYear(),
              (
                (props.mode === 'single' ? selectedDate : startDate) ||
                new Date()
              ).getMonth(),
              1
            )),
            (viewMode = 'days'),
            syncYearsStartToCurrent(),
            (isOpen = true))
      "
    >
      <div>
        <Calendar :size="24" />
        <span class="label">
          {{
            props.mode === "single" && selectedDate
              ? selectedDate.toLocaleDateString("en-US", {
                  month: "short",
                  day: "2-digit",
                  year: "numeric",
                })
              : props.mode === "range" && startDate && endDate
              ? `${startDate.toLocaleDateString("en-US", {
                  month: "short",
                  day: "2-digit",
                  year: "numeric",
                })} — ${endDate.toLocaleDateString("en-US", {
                  month: "short",
                  day: "2-digit",
                  year: "numeric",
                })}`
              : props.mode === "range"
              ? "Select range"
              : "Select date"
          }}
        </span>
      </div>
      <div>
        <button
          v-if="
            props.clearable &&
            ((props.mode === 'single' && selectedDate) ||
              (props.mode === 'range' && (startDate || endDate)))
          "
          @click.stop="
            () => {
              props.mode === 'single'
                ? ((selectedDate = null), (isOpen = false))
                : ((startDate = null), (endDate = null), (isOpen = false));

              clearCalendar();
            }
          "
        >
          <X :size="20" />
        </button>
      </div>
    </div>

    <!-- Calendar -->
    <div
      class="calendar"
      v-if="isOpen"
      :class="`placement--${props.placement}`"
    >
      <div class="calendar__header">
        <button
          @click="
            viewMode === 'days'
              ? (currentMonth = new Date(
                  currentMonth.getFullYear(),
                  currentMonth.getMonth() - 1,
                  1
                ))
              : viewMode === 'months'
              ? (currentMonth = new Date(
                  currentMonth.getFullYear() - 1,
                  currentMonth.getMonth(),
                  1
                ))
              : (yearsStart -= 18)
          "
        >
          <ChevronLeft :size="20" />
        </button>

        <span class="label">
          <!-- LEFT -->
          <button
            class="label-btn"
            @click.stop="
              activePane = 'left';
              viewMode = 'months';
            "
          >
            {{ currentMonth.toLocaleDateString("en-US", { month: "long" }) }}
          </button>
          <button
            class="label-btn"
            @click.stop="
              activePane = 'left';
              viewMode = 'years';
            "
          >
            {{ currentMonth.getFullYear() }}
          </button>

          <!-- RIGHT -->
          <template v-if="props.isTwoSided">
            ·
            <button
              class="label-btn"
              @click.stop="
                activePane = 'right';
                viewMode = 'months';
              "
            >
              {{ rightMonth.toLocaleDateString("en-US", { month: "long" }) }}
            </button>
            <button
              class="label-btn"
              @click.stop="
                activePane = 'right';
                viewMode = 'years';
              "
            >
              {{ rightMonth.getFullYear() }}
            </button>
          </template>
        </span>

        <button
          @click="
            viewMode === 'days'
              ? (currentMonth = new Date(
                  currentMonth.getFullYear(),
                  currentMonth.getMonth() + 1,
                  1
                ))
              : viewMode === 'months'
              ? (currentMonth = new Date(
                  currentMonth.getFullYear() + 1,
                  currentMonth.getMonth(),
                  1
                ))
              : (yearsStart += 18)
          "
        >
          <ChevronRight :size="20" />
        </button>
      </div>

      <div class="calendar__months" v-if="viewMode === 'days'">
        <!-- LEFT month -->
        <div class="calendar__month">
          <div class="calendar__days-header">
            <div
              v-for="day in ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']"
              :key="day"
              class="calendar__days-header__item"
            >
              {{ day }}
            </div>
          </div>

          <div class="calendar__days-grid">
            <div
              @click="
                day.date &&
                  day.fullDate &&
                  dayNow(day.fullDate) <= dayNow(new Date()) &&
                  !(
                    props.mode === 'range' &&
                    props.maxDaysRange &&
                    startDate &&
                    !endDate &&
                    Math.abs(dayNow(day.fullDate) - dayNow(startDate)) >=
                      props.maxDaysRange * 86400000
                  ) &&
                  (props.mode === 'single'
                    ? ((selectedDate = day.fullDate), (isOpen = false))
                    : !startDate || endDate
                    ? ((startDate = day.fullDate), (endDate = null))
                    : ((endDate =
                        day.fullDate > startDate ? day.fullDate : startDate),
                      (startDate =
                        day.fullDate < startDate ? day.fullDate : startDate),
                      (isOpen = false)))
              "
              v-for="(day, idx) in calendarDays"
              @mouseenter="hoverDate = day.fullDate ?? null"
              @mouseleave="hoverDate = null"
              :key="day.date ? day.fullDate.getTime() : `empty-left-${idx}`"
              :class="[
                'calendar__days-grid__day',
                {
                  empty: !day.date,
                  disabled:
                    day.fullDate &&
                    (dayNow(day.fullDate) > dayNow(new Date()) ||
                      (props.mode === 'range' &&
                        props.maxDaysRange &&
                        startDate &&
                        !endDate &&
                        Math.abs(dayNow(day.fullDate) - dayNow(startDate)) >=
                          props.maxDaysRange * 86400000)),

                  selected:
                    day.fullDate &&
                    ((props.mode === 'single' &&
                      selectedDate &&
                      dayNow(selectedDate) === dayNow(day.fullDate)) ||
                      (props.mode === 'range' &&
                        ((startDate &&
                          dayNow(startDate) === dayNow(day.fullDate)) ||
                          (endDate &&
                            dayNow(endDate) === dayNow(day.fullDate))))),
                  'in-range':
                    props.mode === 'range' &&
                    startDate &&
                    day.fullDate &&
                    ((endDate &&
                      dayNow(day.fullDate) > dayNow(startDate) &&
                      dayNow(day.fullDate) < dayNow(endDate)) ||
                      (!endDate &&
                        hoverDate &&
                        ((dayNow(hoverDate) > dayNow(startDate) &&
                          dayNow(day.fullDate) > dayNow(startDate) &&
                          dayNow(day.fullDate) <= dayNow(hoverDate)) ||
                          (dayNow(hoverDate) < dayNow(startDate) &&
                            dayNow(day.fullDate) < dayNow(startDate) &&
                            dayNow(day.fullDate) >= dayNow(hoverDate))))),
                  'range-start':
                    props.mode === 'range' &&
                    startDate &&
                    day.fullDate &&
                    dayNow(startDate) === dayNow(day.fullDate),

                  'range-end':
                    props.mode === 'range' &&
                    endDate &&
                    day.fullDate &&
                    dayNow(endDate) === dayNow(day.fullDate),

                  'range-connected-forward':
                    props.mode === 'range' &&
                    startDate &&
                    day.fullDate &&
                    dayNow(startDate) === dayNow(day.fullDate) &&
                    ((endDate && dayNow(endDate) > dayNow(startDate)) ||
                      (!endDate &&
                        hoverDate &&
                        dayNow(hoverDate) > dayNow(startDate))),

                  'range-connected-backward':
                    props.mode === 'range' &&
                    startDate &&
                    day.fullDate &&
                    dayNow(startDate) === dayNow(day.fullDate) &&
                    !endDate &&
                    hoverDate &&
                    dayNow(hoverDate) < dayNow(startDate),
                },
              ]"
            >
              {{ day.date }}
            </div>
          </div>
        </div>

        <!-- RIGHT month -->
        <div v-if="props.isTwoSided" class="calendar__month">
          <div class="calendar__days-header">
            <div
              v-for="day in ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']"
              :key="day"
              class="calendar__days-header__item"
            >
              {{ day }}
            </div>
          </div>

          <div class="calendar__days-grid">
            <div
              @mouseenter="hoverDate = day.fullDate ?? null"
              @mouseleave="hoverDate = null"
              v-for="(day, idx) in calendarDaysRight"
              :key="day.date ? day.fullDate.getTime() : `empty-right-${idx}`"
              @click="
                day.date &&
                  day.fullDate &&
                  dayNow(day.fullDate) <= dayNow(new Date()) &&
                  !(
                    props.mode === 'range' &&
                    props.maxDaysRange &&
                    startDate &&
                    !endDate &&
                    Math.abs(dayNow(day.fullDate) - dayNow(startDate)) >=
                      props.maxDaysRange * 86400000
                  ) &&
                  (props.mode === 'single'
                    ? ((selectedDate = day.fullDate), (isOpen = false))
                    : !startDate || endDate
                    ? ((startDate = day.fullDate), (endDate = null))
                    : ((endDate =
                        day.fullDate > startDate ? day.fullDate : startDate),
                      (startDate =
                        day.fullDate < startDate ? day.fullDate : startDate),
                      (isOpen = false)))
              "
              :class="[
                'calendar__days-grid__day',
                {
                  empty: !day.date,
                  disabled:
                    day.fullDate &&
                    (dayNow(day.fullDate) > dayNow(new Date()) ||
                      (props.mode === 'range' &&
                        props.maxDaysRange &&
                        startDate &&
                        !endDate &&
                        Math.abs(dayNow(day.fullDate) - dayNow(startDate)) >=
                          props.maxDaysRange * 86400000)),

                  selected:
                    day.fullDate &&
                    ((props.mode === 'single' &&
                      selectedDate &&
                      dayNow(selectedDate) === dayNow(day.fullDate)) ||
                      (props.mode === 'range' &&
                        ((startDate &&
                          dayNow(startDate) === dayNow(day.fullDate)) ||
                          (endDate &&
                            dayNow(endDate) === dayNow(day.fullDate))))),
                  'in-range':
                    props.mode === 'range' &&
                    startDate &&
                    day.fullDate &&
                    ((endDate &&
                      dayNow(day.fullDate) > dayNow(startDate) &&
                      dayNow(day.fullDate) < dayNow(endDate)) ||
                      (!endDate &&
                        hoverDate &&
                        ((dayNow(hoverDate) > dayNow(startDate) &&
                          dayNow(day.fullDate) > dayNow(startDate) &&
                          dayNow(day.fullDate) <= dayNow(hoverDate)) ||
                          (dayNow(hoverDate) < dayNow(startDate) &&
                            dayNow(day.fullDate) < dayNow(startDate) &&
                            dayNow(day.fullDate) >= dayNow(hoverDate))))),
                  'range-start':
                    props.mode === 'range' &&
                    startDate &&
                    day.fullDate &&
                    dayNow(startDate) === dayNow(day.fullDate),

                  'range-end':
                    props.mode === 'range' &&
                    endDate &&
                    day.fullDate &&
                    dayNow(endDate) === dayNow(day.fullDate),

                  'range-connected-forward':
                    props.mode === 'range' &&
                    startDate &&
                    day.fullDate &&
                    dayNow(startDate) === dayNow(day.fullDate) &&
                    ((endDate && dayNow(endDate) > dayNow(startDate)) ||
                      (!endDate &&
                        hoverDate &&
                        dayNow(hoverDate) > dayNow(startDate))),

                  'range-connected-backward':
                    props.mode === 'range' &&
                    startDate &&
                    day.fullDate &&
                    dayNow(startDate) === dayNow(day.fullDate) &&
                    !endDate &&
                    hoverDate &&
                    dayNow(hoverDate) < dayNow(startDate),
                },
              ]"
            >
              {{ day.date }}
            </div>
          </div>
        </div>
      </div>

      <div
        v-else-if="viewMode === 'months'"
        class="calendar__picker calendar__picker--months"
      >
        <button
          v-for="(m, i) in [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
          ]"
          :key="m"
          class="calendar__pick-item"
          :class="{
            active:
              activePane === 'left'
                ? i === currentMonth.getMonth()
                : i === rightMonth.getMonth(),
          }"
          @click="
            activePane === 'left'
              ? (currentMonth = new Date(currentMonth.getFullYear(), i, 1))
              : (currentMonth = new Date(currentMonth.getFullYear(), i, 1)),
              (viewMode = 'days')
          "
        >
          {{ m }}
        </button>
      </div>

      <!-- YEARS -->
      <div v-else class="calendar__picker calendar__picker--years">
        <button
          v-for="y in visibleYears"
          :key="y"
          class="calendar__pick-item"
          :class="{
            active:
              activePane === 'left'
                ? y === currentMonth.getFullYear()
                : y === rightMonth.getFullYear(),
          }"
          @click="
            activePane === 'left'
              ? (currentMonth = new Date(y, currentMonth.getMonth(), 1))
              : (currentMonth = new Date(y, rightMonth.getMonth() + 1, 1)),
              (viewMode = 'months')
          "
        >
          {{ y }}
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.c-co-date-picker {
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  width: fit-content;

  &__input {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 9px 12px;
    cursor: pointer;
    background: var(--color-bg-secondary);
    color: var(--color-secondary-label);
    border: 1px solid var(--color-border);
    border-radius: 14px;
    min-width: 270px;

    & div {
      display: flex;
      align-items: center;
      gap: 10px;
      background: transparent;
    }

    &:hover,
    &.active {
      border-color: var(--color-secondary-label);
    }

    .label {
      font-size: 16px;
      font-weight: 600;
      letter-spacing: -0.5px;
      color: var(--color-label);
      background: transparent;
    }

    & button {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--color-secondary-label);
      height: 20px;
    }
  }

  .calendar {
    border-radius: 16px;
    background: var(--color-background);
    box-shadow: 0 2px 20px var(--color-shadow);
    padding: 16px;
    position: absolute;
    z-index: 10;

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;

      .label {
        font-size: 14px;
        font-weight: 600;
        color: var(--color-secondary-label);
      }

      button {
        background: none;
        border: none;
        cursor: pointer;
        color: var(--color-secondary-label);
        height: 20px;
      }
    }

    &__days-header {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 11px;
      margin-bottom: 8px;

      &__item {
        line-height: 12px;
        text-align: center;
        font-size: 10px;
        font-weight: 600;
        color: var(--color-secondary-label);
        padding: 8px 0;
        user-select: none;
      }
    }

    &__months {
      display: flex;
      gap: 16px;

      &__month {
        width: 100%;
      }
    }

    &__days-grid {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      margin-bottom: 8px;

      &__day {
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
        text-align: center;
        color: var(--color-label);
        aspect-ratio: 1;
        cursor: pointer;
        border-radius: 10px;
        transition: all 0.2s;
        padding: 5px;

        &.selected {
          background: var(--color-blue);
          color: var(--color-background);
        }

        &.selected.range-start.range-connected-forward {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }

        &.selected.range-start.range-connected-backward {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }

        &.selected.range-start.range-connected-forward:not(.range-end) {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }

        &.selected.range-end {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }

        &.selected.range-start.range-end {
          border-radius: 10px;
        }

        &.in-range {
          background: var(--color-date-selected-row);
          border-radius: 0;
        }

        &.empty {
          cursor: default;
        }

        &.disabled {
          opacity: 0.35;
          user-select: none;
          cursor: not-allowed;
        }
      }
    }

    .label-btn {
      background: none;
      border: none;
      cursor: pointer;
      font: inherit;
      color: var(--color-secondary-label);
      font-weight: 600;
      padding: 2px 6px;
      border-radius: 8px;

      &:hover {
        background: rgba(0, 0, 0, 0.05);
      }
    }

    &__picker {
      display: grid;
      gap: 12px;

      &--months {
        grid-template-columns: repeat(3, 1fr);
      }

      &--years {
        grid-template-columns: repeat(3, 1fr);
        max-height: 240px;
        overflow: auto;
        padding-right: 4px;
      }
    }

    .calendar__pick-item {
      border: none;
      cursor: pointer;
      padding: 10px 12px;
      border-radius: 10px;
      font-weight: 600;
      background: var(--color-bg-secondary);
      color: var(--color-label);

      &.active {
        background: var(--color-blue);
        color: var(--color-white);
      }
    }

    &.placement--bottom {
      top: calc(100% + 8px);
      left: 0;
    }

    &.placement--top {
      bottom: calc(100% + 8px);
      left: 0;
    }

    &.placement--right {
      left: calc(100% + 8px);
      top: 0;
    }

    &.placement--left {
      right: calc(100% + 8px);
      top: 0;
    }
  }
}
</style>
